import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Images, Loader2, Camera, ExternalLink } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { pastWorkApi, fileUrl } from "@/lib/api";

function coverOf(item) {
  if (item.cover_image) return item.cover_image;
  const img = (item.gallery || []).find((g) => g.type !== "video");
  return img ? fileUrl(img.url) : "";
}

export default function PastWorkManage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await pastWorkApi.listAll());
    } catch {
      toast.error("Failed to load past work");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (item) => {
    setBusy(item.id);
    try {
      const { id, created_at, ...rest } = item;
      await pastWorkApi.update(item.id, { ...rest, visible: !item.visible });
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, visible: !x.visible } : x)));
      toast.success(item.visible ? "Hidden from public page" : "Now visible on public page");
    } catch {
      toast.error("Could not update visibility");
    } finally {
      setBusy(null);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    await pastWorkApi.remove(item.id);
    toast.success("Past work deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Portfolio</p>
            <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">Past Work</h1>
            <p className="mt-1 text-sm text-muted-foreground">Showcase the events Godinga has delivered. Only visible items appear on the public page.</p>
          </div>
          <div className="flex gap-2">
            <a href="/past-work" target="_blank" rel="noreferrer" data-testid="view-public-pastwork" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold transition-colors hover:bg-secondary">
              <ExternalLink className="h-4 w-4" /> View public page
            </a>
            <button onClick={() => navigate("/past-work/new")} data-testid="add-past-work-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">
              <Plus className="h-4 w-4" /> Add Past Event
            </button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-secondary/60" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
              <Camera className="mx-auto h-10 w-10 text-gold" />
              <h3 className="mt-4 font-serif text-2xl font-medium">No past events yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Add your first event to start building your portfolio.</p>
              <button onClick={() => navigate("/past-work/new")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
                <Plus className="h-4 w-4" /> Add Past Event
              </button>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="past-work-manage-grid">
              {items.map((item) => {
                const cover = coverOf(item);
                return (
                  <div key={item.id} data-testid={`pw-manage-card-${item.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:shadow-lg">
                    <div className="relative aspect-[4/3] bg-secondary">
                      {cover ? <img src={cover} alt={item.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Images className="h-8 w-8 text-muted-foreground" /></div>}
                      <span className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">{item.category}</span>
                      <span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${item.visible ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"}`}>
                        {item.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}{item.visible ? "Visible" : "Hidden"}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-serif text-lg font-semibold leading-snug">{item.title}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">{[item.event_date, item.location].filter(Boolean).join(" · ") || "—"}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{(item.gallery || []).length} photo{(item.gallery || []).length === 1 ? "" : "s"}</p>
                      <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                        <button onClick={() => navigate(`/past-work/${item.id}/edit`)} data-testid={`pw-edit-${item.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"><Pencil className="h-4 w-4" /> Edit</button>
                        <button onClick={() => toggleVisible(item)} disabled={busy === item.id} data-testid={`pw-toggle-${item.id}`} title={item.visible ? "Hide" : "Show"} className="rounded-xl border border-border p-2.5 hover:bg-secondary">
                          {busy === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : item.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button onClick={() => del(item)} data-testid={`pw-delete-${item.id}`} title="Delete" className="rounded-xl border border-border p-2.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
